import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";

import ButtonLink from "~/components/links/ButtonLink";

import GatedDocumentButton from "~/components/buttons/GatedDocumentButton";

import BaseModal from "~/components/BaseModal";
import ErrorBoundary from "~/components/ErrorBoundary";
import VideoEmbed from "~/components/VideoEmbed";

import Form from "~/queryBlocks/Form";

import buttonStyles from "~/styles/buttonStyles";

import { getVideoInfo } from "~/utilities/helpers";
import FaIcon from "~/components/FaIcon";

export const Icon = styled(FaIcon)`
    font-size: 2rem;
    margin-right: 10px;
`;

const ModalButton = styled.button`
    ${buttonStyles};
`;

const ActionButton = ({
    action,
    color,
    doc = null,
    lang,
    linkLabel,
    linkUrl = "",
    isEmbed = false,
    videoType = "media",
    downloadVideo = null,
    video = null,
    videoUrl = "",
    formId = null,
    useIcon = false,
    onClick = () => {},
    slideIndex = 0,
    title = "",
}) => {
    const [openModal, setOpenModal] = useState(null);
    const videoInfo = getVideoInfo(
        isEmbed,
        video,
        videoUrl,
        videoType,
        downloadVideo,
    );

    const linkClick = () => {
        if (onClick) {
            onClick(title, slideIndex, linkUrl);
        }
    };

    const videoButtonClick = () => {
        setOpenModal("video");

        if (onClick) {
            onClick(title, slideIndex, videoInfo.url);
        }
    };

    return action === "link" ? (
        <ButtonLink {...{ color, lang }} to={linkUrl} onClick={linkClick}>
            {useIcon ? <Icon name="link" iconLibrary="fas" /> : null}
            {linkLabel}
        </ButtonLink>
    ) : action === "video" && videoInfo ? (
        <ErrorBoundary>
            <ModalButton
                type="button"
                aria-label={`Watch video of ${linkLabel}`}
                {...{ color, lang }}
                onClick={videoButtonClick}
            >
                {useIcon ? <Icon name="play" iconLibrary="fas" /> : null}
                {linkLabel}
            </ModalButton>
            <BaseModal
                isOpen={openModal === "video"}
                onRequestClose={() => setOpenModal(null)}
            >
                <VideoEmbed
                    url={videoInfo.url}
                    isEmbed={videoInfo.isEmbed}
                    poster={videoInfo.poster}
                    title={linkLabel}
                />
            </BaseModal>
        </ErrorBoundary>
    ) : action === "form" ? (
        <ErrorBoundary>
            <Form
                {...{ formId }}
                modalForm={true}
                isModalOpenProp={openModal === "form"}
                closeModalProp={() => setOpenModal(null)}
                useChildButton={true}
            >
                <ModalButton
                    type="button"
                    aria-label={`Open form for ${linkLabel}`}
                    {...{ color, lang }}
                    onClick={() => setOpenModal("form")}
                >
                    {useIcon ? <Icon name="list" iconLibrary="fas" /> : null}
                    {linkLabel}
                </ModalButton>
            </Form>
        </ErrorBoundary>
    ) : action === "document" ? (
        <GatedDocumentButton {...{ doc, color, lang }}>
            {useIcon ? <Icon name="download" iconLibrary="fas" /> : null}
            {linkLabel}
        </GatedDocumentButton>
    ) : null;
};

ActionButton.propTypes = {
    action: PropTypes.oneOf(["link", "video", "form", "document"]).isRequired,
    color: PropTypes.string.isRequired,
    linkLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
        .isRequired,
    linkUrl: PropTypes.string,
    isEmbed: PropTypes.bool,
    videoType: PropTypes.oneOf(["media", "download"]),
    downloadVideo: PropTypes.shape({}),
    video: PropTypes.shape({
        posterURL: PropTypes.string,
        mediaItemUrl: PropTypes.string,
    }),
    videoUrl: PropTypes.string,
    formId: PropTypes.number,
    doc: PropTypes.shape({}),
    useIcon: PropTypes.bool,
    onClick: PropTypes.func,
    slideIndex: PropTypes.number,
    title: PropTypes.string,
};

export default ActionButton;
